import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { bool, number, shape, string } from 'prop-types'
import { PureComponent } from 'react'

import {
  FILTER_FLYOUT_CLOSE,
  PRODUCT_FILTER_RESET,
} from 'shared/experiments/components/NewSerp/consts'
import FlyoutFilter from 'shared/experiments/components/NewSerp/FlyoutFilter/FlyoutFilter'
import { withSearchStateHandler } from 'shared/experiments/components/NewSerp/helpers'
import type {
  ShippingOptionAttribute,
  ShippingOptionResetAction,
  ShippingOptionToggleAction,
} from 'shared/experiments/components/NewSerp/types'
import store, { subscribe } from 'shared/store'
import type { InferPropsWithDefaults } from 'shared/utils/propTypes'
import { withI18n } from 'shared/utils/withI18n'
import IconBin from 'views/components/atoms/Icon/IconBin'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { publicRuntimeConfigType } from 'views/utils/propTypes'
import { i18nType } from 'views/utils/propTypes/i18nType'

import {
  SHIPPING_OPTIONS,
  SHIPPING_OPTIONS_RESET_EVENT,
  SHIPPING_OPTIONS_TOGGLE_EVENT,
} from './consts'
import ShippingToggleOption from './ShippingToggleOption'

type Refinement = Omit<ShippingOptionToggleAction, 'type' | 'toggle'>

class ShippingOptions extends PureComponent<ComponentProps, ComponentState> {
  static optionAttributes: ShippingOptionAttribute[] = SHIPPING_OPTIONS.map(
    ({ attribute }) => attribute
  )

  state: ComponentState = {
    refinements: {},
    isAdjusted: Boolean(this.props.active),
  }
  subscriptions: CallableFunction[] = []

  constructor(props: ComponentProps) {
    super(props)

    this.subscriptions.push(
      subscribe.after(SHIPPING_OPTIONS_TOGGLE_EVENT, this.handleOptionCheck)
    )
    this.subscriptions.push(
      subscribe.after(PRODUCT_FILTER_RESET, this.handleResetClick)
    )
  }

  componentWillUnmount() {
    this.subscriptions.forEach(unsub => unsub())
  }

  emptyRefinements() {
    this.setState({ refinements: {}, isAdjusted: false })
  }

  refineOptions = () => {
    const { refinements } = this.state

    Object.values(refinements).forEach(ref => {
      // RAF fixes race conditions in case when both options have to be refined
      requestAnimationFrame(() => ref.refine(ref.value))
    })

    // Flush refinements syncronously at the end of the queue
    requestAnimationFrame(() => this.setState({ refinements: {} }))
  }

  handleOptionCheck = ({ type: _, ...ref }: ShippingOptionToggleAction) => {
    this.setState(
      prevState => {
        return {
          refinements: {
            ...prevState.refinements,
            [ref.attribute]: ref,
          },
          isAdjusted: true,
        }
      },
      () => withSearchStateHandler(this.refineOptions)
    )
  }

  /**
   * So far there are only 2 options, so it is fine to apply refinements synchronously.
   * Due to the `Shipping Options` filter nature it is the only way(?):
   * it cannot be a real connected refinement list due to the set of different attributes
   * compiled in this filter. So both options implemented as toggleRefinements, i.e. they
   * each have their own `refine` function, making it impossible to refine both at the same time.
   */
  handleApplyClick = () => {
    withSearchStateHandler(this.refineOptions)
    store.dispatch({ type: FILTER_FLYOUT_CLOSE })
  }

  handleResetClick = () => {
    // The option component will only respond to the single event once,
    // so the event broadcast to each instance of the option (which has a unique attribute)
    // because of the nature of the component: see docs above
    ShippingOptions.optionAttributes.forEach(attribute =>
      store.dispatch({
        type: SHIPPING_OPTIONS_RESET_EVENT,
        attribute,
      } as ShippingOptionResetAction)
    )

    this.emptyRefinements()
  }

  render() {
    const { i18n, hidden, active, label, attribute, count } = this.props
    const { isAdjusted } = this.state

    const shippingOptionsData = SHIPPING_OPTIONS.filter(
      option => option.id !== 'abo-refinement-control'
    )

    return (
      <FlyoutFilter
        attribute={attribute}
        label={label}
        active={active}
        hidden={hidden}
        count={count}
      >
        <div className="e-RefinementList">
          <ul className="a-list-reset">
            {shippingOptionsData.map(item => (
              <ShippingToggleOption
                {...item}
                key={item.value}
                label={i18n._(item.label)}
                attribute={item.attribute}
              />
            ))}
          </ul>
        </div>

        <div className="u-margin--top u-padding--top u-border--top e-RefinementList__ShippingOptions__resetButton--wrapper">
          <ButtonAsLink
            className={classNames('o-RefinementList__resetButton', {
              'u-font-weight--bold': isAdjusted,
            })}
            onClick={this.handleResetClick}
            disabled={!isAdjusted}
          >
            <TextWithIcon icon={<IconBin size="small" />}>
              <Trans id="searchResults.label.clearFilter" />
            </TextWithIcon>
          </ButtonAsLink>
        </div>
      </FlyoutFilter>
    )
  }
}

const propTypes = {
  i18n: i18nType.isRequired,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  attribute: string.isRequired,
  label: string.isRequired,
  active: bool,
  hidden: bool,
  count: number,
}

const defaultProps = {
  active: false,
  hidden: false,
  count: 0,
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

type ComponentState = {
  refinements: Partial<Record<ShippingOptionAttribute, Refinement>>
  isAdjusted: boolean
}

export default withI18n()(withShopAndRuntimeConfig(ShippingOptions))
